import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { FiHome, FiShoppingBag, FiSettings, FiBarChart2, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { adminAPI } from '../services/api';
import Loading from '../components/Loading';
import { toast } from 'react-toastify';

// ============ Dashboard Overview ============
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await adminAPI.getDashboard();
        if (data.success) setStats(data.stats);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <Loading message="Loading dashboard..." />;
  if (!stats) return <p>Failed to load dashboard data.</p>;

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Dashboard</h2>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h4>Total Orders</h4>
          <div className="stat-value">{stats.totalOrders}</div>
        </div>
        <div className="stat-card" style={{ '--accent-color': 'var(--accent)' }}>
          <h4>Total Revenue</h4>
          <div className="stat-value">₹{stats.totalRevenue.toLocaleString('en-IN')}</div>
        </div>
        <div className="stat-card">
          <h4>Customers</h4>
          <div className="stat-value">{stats.totalUsers}</div>
        </div>
      </div>

      {/* Product Quantities - Manufacturing Planning */}
      <div className="order-card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 12, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: 8 }}>
          🏭 Manufacturing Planning
        </h3>
        <p style={{ fontSize: '0.84rem', color: 'var(--text-light)', marginBottom: 15 }}>
          Total quantity needed per product
        </p>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Total Qty (kg)</th>
              <th>Orders</th>
            </tr>
          </thead>
          <tbody>
            {stats.productQuantities.map((pq) => (
              <tr key={pq._id}>
                <td><strong>{pq._id}</strong></td>
                <td style={{ color: 'var(--primary)', fontWeight: 700 }}>{pq.totalQuantityKg} kg</td>
                <td>{pq.totalOrders}</td>
              </tr>
            ))}
            {stats.productQuantities.length === 0 && (
              <tr><td colSpan="3" style={{ textAlign: 'center', color: '#aaa' }}>No orders yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Orders by Status */}
      <div className="order-card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 15, color: 'var(--text-heading)' }}>📋 Orders by Status</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {stats.ordersByStatus.map((s) => (
            <div key={s._id} style={{
              background: 'var(--bg-muted)', padding: '12px 18px', borderRadius: 'var(--radius-md)',
              minWidth: 140, textAlign: 'center', border: '1px solid var(--border-light)',
            }}>
              <div style={{ fontWeight: 800, fontSize: '1.4rem', color: 'var(--primary)', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.03em' }}>{s.count}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-medium)', marginTop: 2 }}>{s._id}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="order-card">
        <h3 style={{ marginBottom: 15, color: 'var(--text-heading)' }}>🕐 Recent Orders</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentOrders.map((order) => (
                <tr key={order._id}>
                  <td><strong style={{ color: 'var(--primary)', fontFamily: 'Inter, sans-serif' }}>{order.orderNumber}</strong></td>
                  <td>{order.user?.name || 'N/A'}</td>
                  <td style={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>₹{order.totalAmount}</td>
                  <td>
                    <span className={`status-badge ${
                      order.status === 'Order Placed' ? 'order-placed' :
                      order.status === 'Delivered' ? 'delivered' :
                      order.status === 'Cancelled' ? 'cancelled' :
                      order.status === 'Dispatched' ? 'dispatched' : 'order-received'
                    }`}>{order.status}</span>
                  </td>
                  <td>
                    <span className={`payment-chip ${
                      order.paymentStatus === 'Paid' ? 'paid' :
                      order.paymentStatus === 'Failed' ? 'failed' : 'pending'
                    }`}>{order.paymentStatus}</span>
                  </td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{new Date(order.createdAt).toLocaleDateString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============ Orders Management ============
const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [dispatchInput, setDispatchInput] = useState({}); // { [orderId]: { trackingId, courierName } }
  const [pendingDispatch, setPendingDispatch] = useState(null); // orderId waiting for tracking
  const [messageModal, setMessageModal] = useState(null); // { orderId, userName, subject, message }
  const [sendingMessage, setSendingMessage] = useState(false);

  const statuses = [
    'all', 'Order Placed', 'Order Received', 'Under Manufacturing',
    'Packed', 'Ready for Dispatch', 'Dispatched', 'Delivered', 'Cancelled',
  ];

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getOrders({ status: statusFilter, paymentStatus: paymentFilter, page });
      if (data.success) {
        setOrders(data.orders);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, paymentFilter, page]);

  const handlePaymentUpdate = async (orderId, paymentStatus) => {
    try {
      await adminAPI.updateOrderPayment(orderId, { paymentStatus });
      toast.success(`Payment marked as "${paymentStatus}"`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update payment');
    }
  };

  const handleStatusUpdate = async (orderId, newStatus, trackingId, courierName) => {
    try {
      await adminAPI.updateOrderStatus(orderId, { status: newStatus, trackingId: trackingId || '', courierName: courierName || '' });
      toast.success(`Order updated to "${newStatus}"`);
      setPendingDispatch(null);
      setDispatchInput((prev) => { const n = { ...prev }; delete n[orderId]; return n; });
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleStatusChange = (orderId, newStatus) => {
    if (newStatus === 'Dispatched') {
      setPendingDispatch(orderId);
      setDispatchInput((prev) => ({ ...prev, [orderId]: { trackingId: '', courierName: '' } }));
    } else {
      handleStatusUpdate(orderId, newStatus);
    }
  };

  const handleSendMessage = async () => {
    if (!messageModal?.message?.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setSendingMessage(true);
    try {
      await adminAPI.sendMessageToUser(messageModal.orderId, {
        subject: messageModal.subject,
        message: messageModal.message,
      });
      toast.success(`Message sent to ${messageModal.userName}!`);
      setMessageModal(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>📦 Order Management</h2>

      {/* Order Status Filters */}
      <div style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {statuses.map((s) => (
          <button
            key={s}
            className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            style={{ fontSize: '0.78rem' }}
          >
            {s === 'all' ? 'All Statuses' : s}
          </button>
        ))}
      </div>

      {/* Payment Filters */}
      <div style={{ marginBottom: 20, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.8rem', color: '#888', fontWeight: 600 }}>💳 Payment:</span>
        {['all', 'Pending', 'Paid', 'Failed'].map((p) => (
          <button
            key={p}
            className={`btn btn-sm ${paymentFilter === p ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setPaymentFilter(p); setPage(1); }}
            style={{ fontSize: '0.78rem' }}
          >
            {p === 'all' ? 'All Payments' : p}
          </button>
        ))}
      </div>

      {loading ? <Loading /> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order._id}>
                  <td><strong>{order.orderNumber}</strong></td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{order.user?.name || 'N/A'}</div>
                    {order.user?.email && (
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>
                        ✉️ <a href={`mailto:${order.user.email}`} style={{ color: '#888', textDecoration: 'none' }}>{order.user.email}</a>
                      </div>
                    )}
                    {/* Show order-level phone first, fallback to user profile phone */}
                    {(order.phone || order.user?.phone) && (
                      <div style={{ fontSize: '0.75rem', color: '#555', fontWeight: 600, marginTop: 2 }}>
                        📞 <a href={`tel:${order.phone || order.user.phone}`} style={{ color: '#555', textDecoration: 'none' }}>{order.phone || order.user.phone}</a>
                      </div>
                    )}
                    {/* Shipping Address */}
                    {(order.shippingAddress?.street || order.shippingAddress?.city) && (
                      <div style={{
                        marginTop: 5,
                        padding: '5px 8px',
                        background: 'var(--bg-muted)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.73rem',
                        color: 'var(--text-medium)',
                        lineHeight: 1.5,
                      }}>
                        📍
                        {order.shippingAddress.street && <span> {order.shippingAddress.street},</span>}
                        {order.shippingAddress.city && <span> {order.shippingAddress.city},</span>}
                        {order.shippingAddress.state && <span> {order.shippingAddress.state}</span>}
                        {order.shippingAddress.pincode && <span> - {order.shippingAddress.pincode}</span>}
                      </div>
                    )}
                    {/* Notes */}
                    {order.notes && (
                      <div style={{ marginTop: 4, fontSize: '0.72rem', color: '#e67e22', fontStyle: 'italic' }}>
                        📝 {order.notes}
                      </div>
                    )}
                    {/* Send Message Button */}
                    <button
                      className="btn btn-sm"
                      style={{ marginTop: 6, fontSize: '0.7rem', padding: '3px 10px', background: '#5C6BC0', color: 'white', display: 'flex', alignItems: 'center', gap: 4 }}
                      onClick={() => setMessageModal({ orderId: order._id, userName: order.user?.name || 'Customer', subject: `Regarding your order ${order.orderNumber}`, message: '' })}
                    >
                      ✉️ Send Message
                    </button>
                  </td>
                  <td>
                    {order.items.map((item, idx) => (
                      <div key={idx} style={{ fontSize: '0.8rem' }}>
                        {item.productName}: {item.quantityKg}kg
                      </div>
                    ))}
                  </td>
                  <td style={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>₹{order.totalAmount}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span className={`payment-chip ${
                        order.paymentStatus === 'Paid' ? 'paid' :
                        order.paymentStatus === 'Failed' ? 'failed' : 'pending'
                      }`}>
                        {order.paymentStatus}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{order.paymentMethod || 'PAY_LATER'}</span>
                      {/* Admin payment controls — available for all payment methods */}
                      {order.paymentStatus !== 'Paid' && (
                        <button
                          className="btn btn-sm"
                          style={{ fontSize: '0.7rem', padding: '2px 8px', background: '#4CAF50', color: 'white', marginTop: 2 }}
                          onClick={() => handlePaymentUpdate(order._id, 'Paid')}
                        >
                          <FiCheckCircle size={11} /> Mark Paid
                        </button>
                      )}
                      {order.paymentStatus === 'Paid' && (
                        <button
                          className="btn btn-sm"
                          style={{ fontSize: '0.7rem', padding: '2px 8px', background: '#EF5350', color: 'white', marginTop: 2 }}
                          onClick={() => handlePaymentUpdate(order._id, 'Pending')}
                        >
                          <FiXCircle size={11} /> Mark Unpaid
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order._id, e.target.value)}
                    >
                      {statuses.filter((s) => s !== 'all').map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {pendingDispatch === order._id && (
                      <div style={{ marginTop: 8, padding: '12px 14px', background: 'var(--bg-muted)', border: '1px solid var(--border-warm)', borderRadius: 'var(--radius-sm)' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-medium)', display: 'flex', alignItems: 'center', gap: 4 }}>🚚 Dispatch Details</div>
                        <input
                          type="text"
                          placeholder="Courier name (e.g. DTDC, BlueDart)"
                          value={dispatchInput[order._id]?.courierName || ''}
                          onChange={(e) => setDispatchInput((prev) => ({ ...prev, [order._id]: { ...prev[order._id], courierName: e.target.value } }))}
                          style={{ width: '100%', marginBottom: 6, fontSize: '0.82rem', padding: '7px 10px', border: '1.5px solid var(--border-warm)', borderRadius: 'var(--radius-sm)', outline: 'none' }}
                          autoFocus
                        />
                        <input
                          type="text"
                          placeholder="Tracking ID (e.g. DTDC1234567890)"
                          value={dispatchInput[order._id]?.trackingId || ''}
                          onChange={(e) => setDispatchInput((prev) => ({ ...prev, [order._id]: { ...prev[order._id], trackingId: e.target.value } }))}
                          style={{ width: '100%', marginBottom: 8, fontSize: '0.82rem', padding: '7px 10px', border: '1.5px solid var(--border-warm)', borderRadius: 'var(--radius-sm)', outline: 'none' }}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn btn-sm btn-primary"
                            style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                            onClick={() => handleStatusUpdate(order._id, 'Dispatched', dispatchInput[order._id]?.trackingId, dispatchInput[order._id]?.courierName)}
                          >
                            ✅ Confirm
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                            onClick={() => { setPendingDispatch(null); setDispatchInput((prev) => { const n = { ...prev }; delete n[order._id]; return n; }); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {order.status === 'Dispatched' && order.trackingId && pendingDispatch !== order._id && (
                      <div style={{ marginTop: 5, fontSize: '0.75rem', color: '#1565C0', fontWeight: 600 }}>
                        {order.courierName && <span>{order.courierName} · </span>}
                        🚚 {order.trackingId}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {new Date(order.createdAt).toLocaleDateString('en-IN')}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan="7" style={{ textAlign: 'center', color: '#aaa', padding: 30 }}>No orders found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Send Message Modal */}
      {messageModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            background: 'white', borderRadius: 'var(--radius-lg)', padding: 28,
            width: '100%', maxWidth: 500, boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          }}>
            <h3 style={{ margin: '0 0 4px', color: 'var(--text-heading)' }}>✉️ Send Message</h3>
            <p style={{ margin: '0 0 20px', fontSize: '0.82rem', color: '#888' }}>
              Sending to: <strong>{messageModal.userName}</strong>
            </p>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ fontWeight: 600, fontSize: '0.83rem', color: 'var(--text-medium)' }}>Subject</label>
              <input
                type="text"
                value={messageModal.subject}
                onChange={(e) => setMessageModal({ ...messageModal, subject: e.target.value })}
                style={{ marginTop: 6, width: '100%', padding: '9px 12px', border: '1.5px solid var(--border-warm)', borderRadius: 'var(--radius-sm)', fontSize: '0.88rem', outline: 'none' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, fontSize: '0.83rem', color: 'var(--text-medium)' }}>Message</label>
              <textarea
                rows={5}
                placeholder="Type your message here..."
                value={messageModal.message}
                onChange={(e) => setMessageModal({ ...messageModal, message: e.target.value })}
                style={{ marginTop: 6, width: '100%', padding: '9px 12px', border: '1.5px solid var(--border-warm)', borderRadius: 'var(--radius-sm)', fontSize: '0.88rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setMessageModal(null)} disabled={sendingMessage}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSendMessage}
                disabled={sendingMessage}
              >
                {sendingMessage ? '⏳ Sending...' : '📨 Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Settings ============
const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await adminAPI.getSettings();
        if (data.success) setSettings(data.settings);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings({
        orderWindowOpen: settings.orderWindowOpen,
        orderWindowStart: null,
        orderWindowEnd: null,
        orderWindowMessage: settings.orderWindowMessage,
        bannerMessage: settings.bannerMessage,
        advancePaymentPercentage: settings.advancePaymentPercentage,
        contactEmail: settings.contactEmail,
        contactPhone: settings.contactPhone,
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading message="Loading settings..." />;
  if (!settings) return <p>Failed to load settings.</p>;

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>⚙️ Settings</h2>

      <div className="checkout-form">
        {/* Order Window */}
        <h3 style={{ marginBottom: 16, color: 'var(--text-heading)', paddingBottom: 12, borderBottom: '1px solid var(--border-light)' }}>📅 Order Window</h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: settings.orderWindowOpen ? '#E8F5E9' : '#FFEBEE', borderRadius: 12, border: `2px solid ${settings.orderWindowOpen ? '#4CAF50' : '#EF5350'}`, transition: 'all 0.3s' }}>
          <div
            onClick={() => setSettings({ ...settings, orderWindowOpen: !settings.orderWindowOpen })}
            style={{ position: 'relative', width: 52, height: 28, background: settings.orderWindowOpen ? '#4CAF50' : '#ccc', borderRadius: 14, cursor: 'pointer', transition: 'background 0.3s', flexShrink: 0 }}
          >
            <div style={{ position: 'absolute', top: 3, left: settings.orderWindowOpen ? 27 : 3, width: 22, height: 22, background: '#fff', borderRadius: '50%', transition: 'left 0.3s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: settings.orderWindowOpen ? '#2E7D32' : '#C62828' }}>
              {settings.orderWindowOpen ? '🟢 Order Window is OPEN' : '🔴 Order Window is CLOSED'}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#888', marginTop: 3 }}>
              {settings.orderWindowOpen ? 'Customers can place orders right now.' : 'Customers cannot place orders.'}
            </div>
          </div>
        </div>

        {settings.orderWindowOpen && (
          <div className="form-group" style={{ marginTop: 16 }}>
            <label style={{ fontWeight: 600, color: '#2C1810' }}>📢 Message to Customers (shown on homepage)</label>
            <input
              type="text"
              placeholder="e.g. We are closing the window soon, place your order before 5th May!"
              value={settings.orderWindowMessage || ''}
              onChange={(e) => setSettings({ ...settings, orderWindowMessage: e.target.value })}
              style={{ marginTop: 6 }}
            />
            <small style={{ color: '#888', marginTop: 4, display: 'block' }}>Leave empty to show no message.</small>
          </div>
        )}

        <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--border-light)' }} />

        {/* Site Settings */}
        <h3 style={{ marginBottom: 16, color: 'var(--text-heading)' }}>🌐 Site Settings</h3>

        <div className="form-group">
          <label>Banner Message</label>
          <input
            type="text"
            value={settings.bannerMessage}
            onChange={(e) => setSettings({ ...settings, bannerMessage: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Advance Payment Percentage (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            value={settings.advancePaymentPercentage}
            onChange={(e) => setSettings({ ...settings, advancePaymentPercentage: parseInt(e.target.value) })}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Contact Email</label>
            <input
              type="email"
              value={settings.contactEmail || ''}
              onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Contact Phone</label>
            <input
              type="tel"
              value={settings.contactPhone || ''}
              onChange={(e) => setSettings({ ...settings, contactPhone: e.target.value })}
            />
          </div>
        </div>

        <button
          className={`btn btn-success mt-20 ${saving ? 'btn-disabled' : ''}`}
          style={{ width: '100%' }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⏳ Saving...' : '💾 Save Settings'}
        </button>
      </div>
    </div>
  );
};

// ============ Admin Layout ============
const AdminPage = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const links = [
    { to: '/admin', icon: <FiBarChart2 />, label: 'Dashboard', exact: true },
    { to: '/admin/orders', icon: <FiShoppingBag />, label: 'Orders' },
    { to: '/admin/settings', icon: <FiSettings />, label: 'Settings' },
    { to: '/', icon: <FiHome />, label: 'Back to Store' },
  ];

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div style={{ padding: '0 20px 16px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 8 }}>
          Admin Panel
        </div>
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={
              link.exact
                ? currentPath === link.to ? 'active' : ''
                : currentPath.startsWith(link.to) && link.to !== '/' ? 'active' : ''
            }
          >
            {link.icon} {link.label}
          </Link>
        ))}
      </aside>
      <main className="admin-content">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminPage;
